import "keen-slider/keen-slider.min.css";
import KeenSlider from "keen-slider";
import "imagesloaded";

/**
  Represents a product image gallery.
  @property {boolean} slider - The slider instance of KeenSlider.
  @property {number} breakpoint - The breakpoint where the slider will be created.
  @property {number} activeIdx - The index of the active slide.
  @property {HTMLElement} container - The container element that holds the slides.
  @property {NodeListOf<HTMLElement>} slides - The slides of the gallery.
*/
export class ProductGallery {
  constructor() {
    this.slider = false;
    this.breakpoint = 1024;
    this.activeIdx = 0;
    this.container = document.querySelector("product-gallery");
    this.slides = document.querySelectorAll("product-gallery-slide");
  }

  /**
    Builds a new instance of the KeenSlider and assigns it to the slider property of the ProductGallery instance.
  */
  _build = () => {
    this.slider = new KeenSlider(this.container, {
      selector: this.slides,
      mode: "free-snap",
      slides: {
        perView: "auto",
        spacing: 0,
      },
      destroyed: async () => {
        this.slider = false;
        const slides = await this._resetAllSlides();
        return slides;
      },
      slideChanged: (slider) => {
        this._updateSlider(slider);
      },
    });
  };

  /**
    Loads videos and reveals them after images are loaded.
  */
  _loadVideos = () => {
    const videos = this.container.querySelectorAll(".product-gallery__video");

    // reveal videos after images are loaded
    videos.forEach((el) => {
      el.style.opacity = 1;
    });
  };

  /**
    Updates the slider by changing the aria-hidden attribute of the old and new slides
    and updating the activeIdx with the new index of the slide.

    @param {object} slider - The KeenSlider instance.
  */
  _updateSlider = (slider) => {
    const oldIdx = this.activeIdx;
    const newIdx = slider.track.details.rel;

    this.slides[oldIdx].setAttribute("aria-hidden", true);
    this.slides[newIdx].setAttribute("aria-hidden", false);

    this.activeIdx = slider.track.details.rel;
  };

  /**
   * Resets all the slides by setting 'aria-hidden' to false
    @param {HTMLElement} el - The element to be reset. Default is 'this.slides'
    @returns {NodeListOf<HTMLElement>} - Returns a node list of all the slides
   */
  _resetAllSlides = (el) => {
    this.slides.forEach((slide) => {
      slide.setAttribute("aria-hidden", false);
    });
    return this.slides;
  };

  /**
    Sets the 'aria-hidden' and 'aria-label' attributes on all slides, and calls the '_build' function
  */
  _setAttributes = () => {
    this.slides.forEach((slide, index) => {
      const isHidden = index !== this.activeIdx;

      slide.setAttribute("aria-hidden", isHidden);
      slide.setAttribute(
        "aria-label",
        `slide ${index + 1} of ${this.slides.length - 1}`
      );
    });

    this._build();
  };

  /**
    Creates a new KeenSlider instance and sets the attributes using the '_setAttributes' function
   */
  _createSlider = () => {
    $(this.container).imagesLoaded(() => {
      this._setAttributes();
    });
  };

  /**
    Sets up the event listener for window resizing and handles slider creation and destruction based on breakpoint.
  */
  _setResize = () => {
    let resizeId;
    let windowWidth = window.innerWidth;

    window.addEventListener("resize", () => {
      clearTimeout(resizeId);
      resizeId = setTimeout(resizedEnded, 500);
    });

    /**
      Called when the window has finished resizing.
      Destroys or creates slider based on window width.
    */
    const resizedEnded = async () => {
      if (
        window.innerWidth < this.breakpoint &&
        window.innerWidth !== windowWidth
      ) {
        windowWidth = window.innerWidth;

        if (this.slider) {
          await this.slider.destroy();
        }

        return;
      }

      if (window.innerWidth > this.breakpoint && this.slider) {
        this.slider.destroy();
        return;
      }

      if (window.innerWidth < this.breakpoint && !this.slider) {
        this._createSlider();
      }
    };
  };

  /**
  Initializes the product gallery by loading videos, creating a slider if necessary based on screen size and number of slides, and setting up resize event listener
  */
  _init = () => {
    this._loadVideos();

    if (window.innerWidth < this.breakpoint && this.slides.length > 1) {
      this._createSlider();
    }

    this._setResize();
  };
}
